import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';
import { Autocomplete, ControlType, DynamicField } from '@totalsoft/rocket-ui';
import { get, over, set } from '@totalsoft/change-tracking-react';
import useQuery from 'hooks/apollo/useQuery';
import { getErrors, isValid } from '@totalsoft/pure-validations-react';
import { PROPERTY_DETAILS } from 'features/items/edit/queries';
import { getDynamicFieldControlType } from 'utils/functions';

const PropertySelector = ({
  lens,
  properties = [],
  validation,
  disabled,
  required,
  propertyIdField = 'propertyId',
  propertyNameField = 'propertyName',
  propertyTypeCodeField = 'propertyTypeCode',
  valueField = 'propertyValue',
  onChange,
  withCustomOnChange = false,
  container,
  propertyProps,
  valueProps
}) => {
  // #region Data loading

  const { data: detailsData } = useQuery(PROPERTY_DETAILS, {
    variables: { propertyId: lens[propertyIdField] |> get },
    skip: !(lens[propertyIdField] |> get)
  });

  const details = useMemo(
    () =>
      (detailsData?.propertyDetails || []).map(({ propertyDetailId, propertyValue }) => ({
        id: propertyDetailId,
        propertyValue
      })),
    [detailsData]
  );

  // #endregion

  const selectedProperty = useMemo(
    () => properties.find(property => property.id === (lens[propertyIdField] |> get)),
    [properties, lens, propertyIdField]
  );

  const selectedDetail = useMemo(
    () => details.find(detail => detail.propertyValue === (lens[valueField] |> get)),
    [details, lens, valueField]
  );

  // #region Props handling

  const internalDisabled = useMemo(() => {
    if (disabled && typeof disabled === 'object') {
      return { property: false, value: false, ...disabled };
    }
    if (typeof disabled === 'boolean') return { property: disabled, value: disabled };
    return { property: false, value: false };
  }, [disabled]);

  const internalRequired = useMemo(() => {
    if (required && typeof required === 'object') {
      return { property: false, value: false, ...required };
    }
    if (typeof required === 'boolean') return { property: required, value: false }; // leave value non-required by default
    return { property: false, value: false };
  }, [required]);

  const PropertyContainer = useMemo(() => container?.property || container || Box, [container]);

  const ValueContainer = useMemo(() => container?.value || container || Box, [container]);

  // #endregion

  // #region Handlers

  const getFilteredProperties = useCallback(
    search => {
      if (!search) return Promise.resolve([]);
      const filteredProperties = properties.filter(({ name }) => name.toLowerCase().includes(search.toLowerCase()));
      return Promise.resolve(filteredProperties);
    },
    [properties]
  );

  const handlePropertyChange = value => {
    if (onChange) {
      onChange('property', value);
      if (withCustomOnChange) return;
    }
    over(lens, prev => ({
      ...prev,
      [propertyIdField]: value.id,
      [propertyNameField]: value.name,
      [propertyTypeCodeField]: value.typeCode,
      [valueField]: null
    }));
  };

  const handleValueChange = value => {
    if (onChange) {
      onChange('value', value, selectedProperty?.typeCode, details);
      if (withCustomOnChange) return;
    }
    if (value && typeof value === 'object' && !(value instanceof Date)) {
      (lens[valueField] |> set)(value.propertyValue);
    } else {
      (lens[valueField] |> set)(value);
    }
  };

  // #endregion

  const getProperty = () => {
    return { id: lens[propertyIdField] |> get, name: (lens[propertyNameField] |> get) || selectedProperty?.name };
  };

  const getValue = () => {
    if (details && details.length > 0) {
      if (selectedDetail) return selectedDetail;
      return { id: lens[valueField] |> get, propertyValue: lens[valueField] |> get };
    }
    return lens[valueField] |> get;
  };

  const getControlType = () => {
    if (details && details.length > 0) {
      return ControlType.Autocomplete;
    }
    return getDynamicFieldControlType((lens[propertyTypeCodeField] |> get) || selectedProperty?.typeCode);
  };

  return (
    <>
      <PropertyContainer>
        <Autocomplete
          value={getProperty()}
          onChange={handlePropertyChange}
          loadOptions={getFilteredProperties}
          error={!isValid(validation[propertyIdField])}
          helperText={getErrors(validation[propertyIdField])}
          required={internalRequired.property}
          disabled={internalDisabled.property}
          {...propertyProps}
        />
      </PropertyContainer>
      <ValueContainer>
        <DynamicField
          controlType={getControlType()}
          value={getValue()}
          onChange={handleValueChange}
          options={details}
          labelKey='propertyValue'
          error={!isValid(validation[valueField])}
          helperText={getErrors(validation[valueField])}
          required={internalRequired.value}
          disabled={internalDisabled.value}
          {...valueProps}
        />
      </ValueContainer>
    </>
  );
};

PropertySelector.propTypes = {
  lens: PropTypes.object,
  properties: PropTypes.array,
  validation: PropTypes.object,
  disabled: PropTypes.oneOfType([PropTypes.bool, PropTypes.shape({ property: PropTypes.bool, value: PropTypes.bool })]),
  required: PropTypes.oneOfType([PropTypes.bool, PropTypes.shape({ property: PropTypes.bool, value: PropTypes.bool })]),
  propertyIdField: PropTypes.string,
  propertyNameField: PropTypes.string,
  propertyTypeCodeField: PropTypes.string,
  valueField: PropTypes.string,
  onChange: PropTypes.func,
  withCustomOnChange: PropTypes.bool,
  container: PropTypes.oneOfType([PropTypes.node, PropTypes.shape({ property: PropTypes.node, value: PropTypes.node })]),
  propertyProps: PropTypes.object,
  valueProps: PropTypes.object
};

export default PropertySelector;
